'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { useSpring } from 'react-spring';
import { MousePointer } from 'lucide-react';

import {
  clamp,
  clampedNormalize,
  roundTo,
  roundToNearest,
} from '@/utils';
import { BREAKPOINTS } from '@/constants';
import useBoundingBox from '@/hooks/use-bounding-box';

import Code from './Code';
import Box from './Box';

const resetTimingFunction = 'cubic-bezier(0.25, 0.05, 0.1, 1.05)';

/**
 * Statuses:
 * • initial —  no light, element is inert.
 * • active  —  Light follows cursor, can be moved with arrow keys
 *              while focused
 * • aborted —  Mouse has left the arena, though the light is still
 *              following briefly
 * • reset   —  The light is moved to the center, and a transition
 *              is added for smooth effect.
 */

function GrabbableLightDemo({ variant, distance = 1 }) {
  const wrapperRef = React.useRef();
  const cardRef = React.useRef();
  const timeoutId = React.useRef();

  const wrapperBB = useBoundingBox(wrapperRef);
  const wrapperBBRef = React.useRef(wrapperBB);
  wrapperBBRef.current = wrapperBB;

  const [status, setStatus] = React.useState('initial');

  const [mousePosition, setMousePosition] = React.useState({});

  React.useEffect(() => {
    let timeoutId;

    const updateMousePosition = (ev) => {
      if (!wrapperBB) {
        return;
      }

      const isOutOfBounds =
        ev.clientX < wrapperBB.left ||
        ev.clientX > wrapperBB.left + wrapperBB.width ||
        ev.clientY < wrapperBB.top ||
        ev.clientY > wrapperBB.top + wrapperBB.height;

      // Ignore mouse-moves outside the box, when the element has
      // been reset.
      if (status === 'reset' && isOutOfBounds) {
        return;
      }

      if (status === 'active' && isOutOfBounds) {
        setStatus('aborted');
      }

      if (status === 'reset' && !isOutOfBounds) {
        setStatus('active');
      }

      setMousePosition({
        x: ev.clientX - wrapperBB.left,
        y: ev.clientY - wrapperBB.top,
      });
    };

    window.addEventListener('mousemove', updateMousePosition);

    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      window.clearTimeout(timeoutId);
    };
  }, [status, wrapperBB]);

  React.useEffect(() => {
    // Pluck from ref to avoid needing to list as a dependency.
    // I don't know if this is *actually* required; I'm revisiting this years later, fixing lint warnings. This is the quickest fix.
    const wrapperBB = wrapperBBRef.current;

    if (status === 'aborted') {
      timeoutId.current = window.setTimeout(() => {
        setStatus('reset');
        setMousePosition({
          x: wrapperBB.width / 2,
          y: 48,
        });
      }, 1000);
    } else if (status === 'active') {
      window.clearTimeout(timeoutId.current);
    }
  }, [status]);

  const boxShadow = getShadow(
    status,
    mousePosition,
    cardRef,
    wrapperBB
  );

  function activate() {
    if (status === 'initial') {
      setStatus('active');
    }
  }

  function handleKeydown(ev) {
    switch (ev.key) {
      case 'ArrowUp': {
        ev.preventDefault();
        return setMousePosition({
          x: mousePosition.x,
          y: clamp(mousePosition.y - 20, 0, wrapperBB.height),
        });
      }
      case 'ArrowLeft': {
        ev.preventDefault();
        return setMousePosition({
          x: clamp(mousePosition.x - 20, 0, wrapperBB.width),
          y: mousePosition.y,
        });
      }
      case 'ArrowRight': {
        ev.preventDefault();
        return setMousePosition({
          x: clamp(mousePosition.x + 20, 0, wrapperBB.width),
          y: mousePosition.y,
        });
      }
      case 'ArrowDown': {
        ev.preventDefault();
        return setMousePosition({
          x: mousePosition.x,
          y: clamp(mousePosition.y + 20, 0, wrapperBB.height),
        });
      }
    }
  }

  function handleTouchStart(ev) {
    if (status === 'initial') {
      setStatus('active');
    }
    if (ev.touches.length > 0 && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const touch = ev.touches[0];
      setMousePosition({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    }
  }

  function handleTouchMove(ev) {
    if (ev.touches.length > 0 && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const touch = ev.touches[0];
      setMousePosition({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    }
  }

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={activate}
      onFocus={() => {
        if (status === 'initial') {
          setStatus('active');
          setMousePosition({
            x: wrapperBB.width / 2,
            y: 48,
          });
        } else {
          activate();
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onKeyDown={handleKeydown}
      onBlur={() => {
        setStatus('reset');
      }}
    >
      <Card
        ref={cardRef}
        style={{
          '--drop-shadow': `drop-shadow(${boxShadow})`,
          '--box-shadow': boxShadow,
          transition:
            status === 'reset'
              ? `filter 1000ms ${resetTimingFunction}`
              : 'none',
        }}
      />
      <Light
        style={{
          opacity: status === 'initial' ? 0 : 1,
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
          transition:
            status === 'reset'
              ? `transform 1000ms ${resetTimingFunction}`
              : 'none',
        }}
      />
      <InteractiveNotice
        style={{ opacity: status === 'initial' ? 1 : 0 }}
      >
        <MousePointer size={16} /> Hover, focus, or tap to interact
      </InteractiveNotice>
    </Wrapper>
  );
}

function getShadow(status, mousePosition, cardRef, wrapperBB) {
  if (status === 'initial') {
    // No shadow at first! Since the light isn't present yet.
    return null;
  }

  const cardBB = cardRef.current?.getBoundingClientRect();

  // No shadow until all refs have been captured.
  if (!cardBB || !wrapperBB) {
    return null;
  }

  const relativeCardBox = {
    x: cardBB.left - wrapperBB.left,
    y: cardBB.top - wrapperBB.top,
  };

  const cardCenter = {
    x: relativeCardBox.x + cardBB.width / 2,
    y: relativeCardBox.y + cardBB.height / 2,
  };

  const xDistance = mousePosition.x - cardCenter.x;
  const yDistance = mousePosition.y - cardCenter.y;

  const xOffset = clampedNormalize(xDistance, 400, -400, -20, 20);
  const yOffset = clampedNormalize(yDistance, 400, -400, -20, 20);

  return `${xOffset}px ${yOffset}px 8px hsl(285deg 30% 40% / 0.5)`;
}

const Wrapper = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  padding: 96px 32px;
  margin: 0 auto 32px;
  width: 100%;
  background-color: hsl(285deg 90% 90%);
  border-radius: 8px;
  border: 2px solid var(--color-text);
  outline-offset: 3px;
  /* Don't allow the light to leave the wrapper */
  overflow: hidden;
  overflow: clip;
  -webkit-tap-highlight-color: transparent;
  touch-action: none;

  @media ${BREAKPOINTS.smAndSmaller} {
    padding: 64px 0px;
    margin-left: 0;
    margin-right: 0;
  }

  html[data-color-mode='dark'] & {
    border: 2px solid transparent;
  }
`;

const Card = styled(Box)`
  width: 100px;
  height: 100px;
  background-color: hsl(285deg 70% 96%);
  border-radius: 6px;
  filter: var(--drop-shadow);
  will-change: transform;
`;

const Light = styled.span`
  --size: 48px;
  position: absolute;
  width: var(--size);
  height: var(--size);
  top: calc(var(--size) / 2 * -1);
  left: calc(var(--size) / 2 * -1);
  background: white;
  border-radius: 50%;
  cursor: grabbing;
  box-shadow: 0px 0px 16px 4px hsl(285deg 80% 96%);
`;

const InteractiveNotice = styled.p`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 16px 24px;
  background: hsl(285deg 80% 99% / 0.5);
  transition: opacity 250ms;
  border-radius: 4px;
  color: hsl(0deg 0% 0% / 0.8);
`;

export default GrabbableLightDemo;
